import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';
import { BotConfigShape } from '../constants/botTypes';

interface WhatsAppConfig {
  _id: string;
  phoneNumber: string;
  phoneNumberId?: string;
  provider?: 'cloud_api' | 'personal_web';
  sessionName?: string;
  sessionStatus?: string;
  pushName?: string;
  numberType: string;
  displayName?: string;
  companyName?: string;
  companyId?: string;
  botConfig?: BotConfigShape;
  status: {
    isActive: boolean;
    isVerified: boolean;
  };
}

interface WhatsAppContextType {
  whatsappNumbers: WhatsAppConfig[];
  loading: boolean;
  error: string | null;
  refetchWhatsAppNumbers: () => Promise<void>;
}

const WhatsAppContext = createContext<WhatsAppContextType | undefined>(undefined);

interface WhatsAppProviderProps {
  children: ReactNode;
}

export const WhatsAppProvider: React.FC<WhatsAppProviderProps> = ({ children }) => {
  const [whatsappNumbers, setWhatsappNumbers] = useState<WhatsAppConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { state: authState } = useAuth();

  const fetchWhatsAppNumbers = async () => {
    if (!authState.user || !authState.isAuthenticated || authState.isLoading) {
      setWhatsappNumbers([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Use different endpoints based on user role
      // Agents and other users use the messages endpoint which filters based on permissions
      // Admins and super admins can still use the admin endpoint
      const endpoint = authState.user.role === 'admin' || authState.user.role === 'super_admin' 
        ? '/admin/whatsapp-configs' 
        : '/messages/whatsapp-configs';
      
      const response = await apiService.get(endpoint);
      const numbersData = Array.isArray(response.data.data) ? response.data.data : [];
      
      setWhatsappNumbers(numbersData);
    } catch (error: any) {
      console.error('Failed to load WhatsApp numbers:', error);
      setError(error.response?.data?.message || 'Failed to load WhatsApp numbers');
      setWhatsappNumbers([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch WhatsApp numbers when user changes or component mounts
  useEffect(() => {
    // Only fetch when authentication is complete and not loading
    if (!authState.isLoading && authState.isAuthenticated && authState.user) {
      fetchWhatsAppNumbers();
    } else if (!authState.isAuthenticated) {
      setWhatsappNumbers([]);
      setError(null);
    }
  }, [authState.user, authState.isAuthenticated, authState.isLoading]);

  const refetchWhatsAppNumbers = async () => {
    await fetchWhatsAppNumbers();
  };

  const contextValue: WhatsAppContextType = {
    whatsappNumbers,
    loading,
    error,
    refetchWhatsAppNumbers,
  };

  return (
    <WhatsAppContext.Provider value={contextValue}>
      {children}
    </WhatsAppContext.Provider>
  );
};

export const useWhatsApp = (): WhatsAppContextType => {
  const context = useContext(WhatsAppContext);
  if (!context) {
    throw new Error('useWhatsApp must be used within a WhatsAppProvider');
  }
  return context;
};

export default WhatsAppContext;
